import { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { Logo } from '../Logo';
import { Mail, Phone, Loader2, ArrowLeft, Sparkles } from 'lucide-react';
import { api } from '../../lib/api';
import { FestiveDecoration } from '../FestiveDecoration';
import { toast } from 'sonner';

interface LoginScreenProps {
  onSuccess: (contact: string) => void;
  onNavigate: (screen: string) => void;
}

export function LoginScreen({ onSuccess, onNavigate }: LoginScreenProps) {
  const [contact, setContact] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [inputType, setInputType] = useState<'email' | 'phone'>('email');

  const detectContactType = (value: string): 'email' | 'phone' => {
    // Simple detection: if it contains @, it's email, otherwise phone
    return value.includes('@') ? 'email' : 'phone';
  };

  const validateContact = (value: string): boolean => {
    const type = detectContactType(value);
    if (type === 'email') {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    } else {
      // Basic phone validation (digits, spaces, dashes, parentheses)
      return /^[\d\s\-\(\)\+]+$/.test(value) && value.replace(/\D/g, '').length >= 10;
    }
  };

  // Normalize phone number: add +1 for 10-digit US/Canada numbers
  const normalizePhoneNumber = (value: string): string => {
    const digitsOnly = value.replace(/\D/g, '');
    // If it's exactly 10 digits and doesn't start with +, assume US/Canada
    if (digitsOnly.length === 10 && !value.trim().startsWith('+')) {
      return '+1' + digitsOnly;
    }
    return value;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!contact.trim()) {
      setError('Please enter your email or phone number');
      return;
    }

    if (!validateContact(contact)) {
      setError('Please enter a valid email address or phone number');
      return;
    }

    setIsLoading(true);

    // Normalize the contact: add +1 for 10-digit phone numbers
    const trimmedContact = contact.trim();
    const normalizedContact = detectContactType(trimmedContact) === 'phone' 
      ? normalizePhoneNumber(trimmedContact)
      : trimmedContact;

    try {
      const currentUrl = window.location.origin;
      console.log('[LoginScreen] Requesting magic link with origin:', currentUrl);
      const response = await api.requestMagicLink(normalizedContact, 'dashboard', currentUrl);
      
      if (response.success) {
        setEmailSent(true);
        onSuccess(normalizedContact);
        toast.success('Magic link sent!', {
          description: `Check your ${inputType === 'email' ? 'inbox' : 'messages'} for the sign-in link.`,
        });
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to send magic link. Please try again.';
      setError(errorMessage);
      toast.error('Failed to send magic link', {
        description: errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleContactChange = (value: string) => {
    setContact(value);
    setInputType(detectContactType(value));
    setError('');
  };

  if (emailSent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-green-50 flex items-center justify-center p-4 relative overflow-hidden">
        <FestiveDecoration />
        
        <Card className="w-full max-w-md border-2 border-emerald-100 shadow-xl relative z-10">
          <CardHeader className="text-center space-y-4 pb-6">
            <div className="flex justify-center mb-2">
              <Logo size="lg" />
            </div>
            <div className="w-16 h-16 mx-auto bg-emerald-100 rounded-full flex items-center justify-center">
              <Mail className="w-8 h-8 text-emerald-600" />
            </div>
            <CardTitle className="text-2xl">Check Your {inputType === 'email' ? 'Email' : 'Phone'}! 📬</CardTitle>
            <CardDescription className="text-base">
              We've sent a magic link to{' '}
              <span className="font-semibold text-emerald-700">{contact}</span>
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-4">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
              <p className="text-sm text-emerald-900 mb-2">
                <strong>Next steps:</strong>
              </p>
              <ol className="text-sm text-emerald-800 space-y-1 list-decimal list-inside">
                <li>Check your {inputType === 'email' ? 'inbox' : 'messages'} for our email</li>
                <li>Click the "Sign In" button</li>
                <li>You'll be automatically signed in!</li>
              </ol>
            </div>

            <Alert>
              <Sparkles className="h-4 w-4" />
              <AlertDescription>
                The link expires in <strong>15 minutes</strong> for your security.
              </AlertDescription>
            </Alert>

            <div className="pt-4 space-y-2">
              <Button
                variant="outline"
                className="w-full"
                onClick={() => setEmailSent(false)}
              >
                Didn't receive it? Try again
              </Button>
              
              <Button
                variant="ghost"
                className="w-full"
                onClick={() => onNavigate('landing')}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-green-50 flex items-center justify-center p-4 relative overflow-hidden">
      <FestiveDecoration />
      
      <Card className="w-full max-w-md border-2 border-emerald-100 shadow-xl relative z-10">
        <CardHeader className="text-center space-y-4 pb-6">
          <div className="flex justify-center mb-2">
            <Logo size="lg" />
          </div>
          <CardTitle className="text-2xl">Welcome! 🎁</CardTitle>
          <CardDescription className="text-base">
            Sign up or sign in with a magic link - no password needed
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="contact" className="text-sm font-medium">
                Email or Phone Number
              </label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  {inputType === 'email' ? (
                    <Mail className="w-4 h-4" />
                  ) : (
                    <Phone className="w-4 h-4" />
                  )}
                </div>
                <Input
                  id="contact"
                  type="text"
                  placeholder="you@example.com or +1234567890"
                  value={contact}
                  onChange={(e) => handleContactChange(e.target.value)}
                  className="pl-10"
                  disabled={isLoading}
                />
              </div>
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending Magic Link...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Send Magic Link
                </>
              )}
            </Button>

            <div className="pt-4">
              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => onNavigate('landing')}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </div>
          </form>

          <div className="mt-6 pt-6 border-t">
            <p className="text-xs text-center text-muted-foreground">
              By signing in, you agree to our{' '}
              <a 
                href="/terms" 
                className="text-emerald-600 hover:text-emerald-700 underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms of Service
              </a>
              {' '}and{' '}
              <a 
                href="/privacy" 
                className="text-emerald-600 hover:text-emerald-700 underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy
              </a>.
              <br />
              <a 
                href="https://www.kingjamesbibleonline.org/Acts-Chapter-20/#35"
                className="text-emerald-600 hover:text-emerald-700 underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                Inspired by Acts 20:35 - "It is more blessed to give than to receive."
              </a>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
